import React from 'react';

const STATUS_COLORS = {
  draft: '#adb5bd',
  sent: '#4f9cf9',
  completed: '#2eb872',
  revoked: '#6c757d',
  declined: '#ef6b6b',
  action_required: '#f0ad4e',
};

function statusColor(status) {
  const key = String(status || '').toLowerCase().replace(/\s+/g, '_');
  return STATUS_COLORS[key] || '#7d59a6';
}

function buildArcs(items) {
  const total = items.reduce((sum, item) => sum + (item.count || 0), 0);
  if (!total) return [];

  let cursor = 0;
  return items.map((item) => {
    const fraction = (item.count || 0) / total;
    const start = cursor;
    cursor += fraction;
    return { ...item, start, end: cursor, fraction, total };
  });
}

const SIZE = 160;
const R = 58;
const CX = SIZE / 2;
const CY = SIZE / 2;

function polarToCartesian(angle) {
  const rad = (angle * 2 * Math.PI) - Math.PI / 2;
  return {
    x: CX + R * Math.cos(rad),
    y: CY + R * Math.sin(rad),
  };
}

function arcPath(startFrac, endFrac) {
  if (endFrac - startFrac >= 1) {
    return `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R} Z`;
  }
  const start = polarToCartesian(startFrac);
  const end = polarToCartesian(endFrac);
  const largeArc = endFrac - startFrac > 0.5 ? 1 : 0;
  return `M ${CX} ${CY} L ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

const TeamStatusDonut = ({ items = [] }) => {
  const [activeStatus, setActiveStatus] = React.useState(null);
  const arcs = React.useMemo(() => buildArcs(items), [items]);
  const total = arcs[0]?.total ?? 0;
  const active = arcs.find((a) => a.status === activeStatus) || null;

  if (!items.length || !total) {
    return (
      <div className="team-status-donut team-status-donut--empty">
        <i className="ri-pie-chart-2-line" />
        <p>No status data.</p>
      </div>
    );
  }

  return (
    <div className="team-status-donut">
      <div className="team-status-donut__chart-wrap">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="team-status-donut__svg" role="img" aria-label="Status breakdown chart">
          {arcs.map((arc) => {
            const isActive = activeStatus === arc.status;
            const dimmed = activeStatus && !isActive;
            return (
              <path
                key={arc.status}
                d={arcPath(arc.start, arc.end)}
                fill={statusColor(arc.status)}
                className={`team-status-donut__slice${isActive ? ' is-active' : ''}${dimmed ? ' is-dimmed' : ''}`}
                onMouseEnter={() => setActiveStatus(arc.status)}
                onMouseLeave={() => setActiveStatus(null)}
                onFocus={() => setActiveStatus(arc.status)}
                onBlur={() => setActiveStatus(null)}
                tabIndex={0}
                role="button"
                aria-label={`${arc.status}: ${arc.count}`}
              />
            );
          })}
          <circle cx={CX} cy={CY} r={36} className="team-status-donut__hole" />
        </svg>
        <div className="team-status-donut__center">
          <strong>{active ? active.count : total}</strong>
          <span>{active ? String(active.status).replace(/_/g, ' ') : 'Total'}</span>
        </div>
      </div>
      <div className="team-status-donut__legend">
        {arcs.map((arc) => {
          const pct = ((arc.count || 0) / total) * 100;
          const isActive = activeStatus === arc.status;
          return (
            <button
              key={arc.status}
              type="button"
              className={`team-status-donut__legend-item${isActive ? ' is-active' : ''}`}
              onMouseEnter={() => setActiveStatus(arc.status)}
              onMouseLeave={() => setActiveStatus(null)}
              onClick={() => setActiveStatus((prev) => (prev === arc.status ? null : arc.status))}
            >
              <span className="team-status-donut__legend-dot" style={{ background: statusColor(arc.status) }} />
              <span className="team-status-donut__legend-label text-capitalize">{arc.status}</span>
              <span className="team-status-donut__legend-value">{arc.count}</span>
              <span className="team-status-donut__legend-pct">{pct.toFixed(0)}%</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TeamStatusDonut;
